import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IframeDialogComponent } from '@components';
import { MobileDirective } from '@directives';
import { environment } from '@environments/environment';
import { CACHE_KEYS } from '@shared/constants';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { BreakpointService } from 'src/app/@shared/services/breakpoint/breakpoint.service';
import { SupportOutageAlert } from './support-outage-banner.interface';
import { SupportOutageBannerService } from './support-outage-banner.service';

@Component({
    selector: 'app-support-outage-banner',
    templateUrl: './support-outage-banner.component.html',
    styleUrls: ['./support-outage-banner.component.scss'],
    imports: [CommonModule, MobileDirective, MatButtonModule, MatDialogModule, MatIconModule, MatTooltipModule],
    providers: [SupportOutageBannerService],
})
export class SupportOutageBannerComponent implements OnInit {
    private bo = inject(BreakpointObserver);
    private dialog = inject(MatDialog);
    private supportOutageService = inject(SupportOutageBannerService);

    statusPageCta = 'Learn more';
    statusTitle = 'Genesys Cloud Conversation Failures: ';
    statusPageUrl = 'https://status.mypurecloud.com/';
    showCTA = true;
    status: SupportOutageAlert;
    supportOutageAlert$: Observable<boolean>;
    timeZone: string;
    mouseover: boolean;
    /**
     * String identifiers for analytics services to track this tile, joined with hyphen in HTML
     */
    dataAnalytics: string[];
    dataAnalyticsClose: string;
    dataAnalyticsLearnMore: string;
    @Output() bannerStateChange = new EventEmitter<boolean>();

    ngOnInit(): void {
        this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.dataAnalyticsClose = ['close', 'modal', this.dataAnalytics].join('-');
        this.dataAnalyticsLearnMore = ['learn_more', 'modal', this.dataAnalytics].join('-');

        const isClosed = localStorage.getItem(CACHE_KEYS.OUTAGE_BANNER_CLOSED) === 'true';
        // const wasShownThisSession = sessionStorage.getItem('outage-banner-shown') === 'true';

        if (isClosed) {
            this.supportOutageAlert$ = of(false);
            this.bannerStateChange.emit(false);
            return;
        }

        this.supportOutageAlert$ = this.supportOutageService.supportOutageAlert$.pipe(
            map((summary) => {
                if (['local', 'dev'].includes(environment.name)) {
                    return true;
                } else if (summary?.components?.length) {
                    const componentName = this.lookupComponentName(this.timeZone);
                    if (!componentName) return false;
                    const hasOutage = summary.components.some(
                        (x) => x?.name?.startsWith(componentName) && x?.status?.toLowerCase().includes('outage'),
                    );
                    if (hasOutage) this.timeZone = componentName;
                    return hasOutage;
                } else {
                    return false;
                }
            }),
            catchError(() => {
                this.statusPageError();
                return of(true);
            }),
        );

        this.supportOutageAlert$.subscribe((isVisible) => {
            this.bannerStateChange.emit(isVisible);
        });
    }

    /**
     * Region name selection according to timezone.
     */
    lookupComponentName(timeZone: string): string {
        const timeZoneStart = timeZone.substring(0, timeZone.indexOf('/'));
        switch (timeZoneStart) {
            case 'America':
            // fall-through
            case 'Atlantic':
                return 'Americas';
            case 'Africa':
            // fall-through
            case 'Europe':
                return 'EMEA';
            case 'Asia':
            // fall-through
            case 'Australia':
            // fall-through
            case 'Indian':
            // fall-through
            case 'Pacific':
                return 'Asia Pacific';
            default:
                return '';
        }
    }

    statusPageError(): void {
        this.statusTitle = 'Genesys Status Page is Inaccessible: ';
        this.showCTA = false;
    }

    handleOpenDialog(): void {
        firstValueFrom(this.bo.observe('(max-width: 768px)').pipe(map((state) => state.matches))).then((isMobile) => {
            this.dialog.open(IframeDialogComponent, {
                width: isMobile ? '' : '80%',
                maxWidth: BreakpointService.contentMaxWidth,
                height: isMobile ? '90%' : '80%',
                autoFocus: false,
                data: {
                    title: this.statusTitle + this.timeZone,
                    subtitle: 'Your region has been detected based on your computer settings.',
                    subtitlePosition: 'bottom',
                    src: this.statusPageUrl,
                    dataAnalytics: ['support', 'outage', 'dialog'],
                },
            });
        });
    }

    closeAlert(): void {
        localStorage.setItem(CACHE_KEYS.OUTAGE_BANNER_CLOSED, 'true');
        this.supportOutageAlert$ = of(false);
        this.bannerStateChange.emit(false);
    }
}
